import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { useCurrentApplication } from '../useCurrentApplication'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  description: z.string().max(200, 'Description cannot exceed 200 characters').optional(),
  organisationId: z.number().min(1, 'Organisation is required'),
  applicationId: z.number().min(1, 'Application is required'),
})

export type ApplicationTeamForm = z.infer<typeof formSchema>

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsApplicationTeam = RestSchemas['Messages.Applications.ApplicationTeam']
type MessagesSubscriptionsNewOrganisationTeam = RestSchemas['Messages.Subscriptions.NewOrganisationTeam']
type MessagesSubscriptionsOrganisation = RestSchemas['Messages.Subscriptions.Organisation']

type ApplicationTeamFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedTeam?: MessagesApplicationsApplicationTeam
  delete?: boolean
}

export const ApplicationTeamForm = (props: ApplicationTeamFormProps) => {
  const { applicationId } = useCurrentApplication()
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedTeam
  const del = edit && props.delete

  const {
    getApplicationsApiGetApplications,
    getApplicationsApiGetApplicationTeamsForUserOrganization,
    getSubscriptionsApiGetOrganisations,
    postApplicationsApiUpdateTeam,
    putApplicationsApiAddTeam,
    deleteApplicationsApiDeleteTeam,
  } = useRestClient()

  const {
    data: applications,
    isError: isApplicationsError,
    isPending: isApplicationsLoading,
  } = getApplicationsApiGetApplications.useQuery<MessagesApplicationsApplication[]>()

  const {
    data: organisations,
    isError: isOrganisationsError,
    isPending: isOrganisationsLoading,
  } = getSubscriptionsApiGetOrganisations.useQuery<MessagesSubscriptionsOrganisation[]>()

  const { mutate: updateTeam } = postApplicationsApiUpdateTeam.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Team successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating team')
    },
  })

  const { mutate: deleteTeam } = deleteApplicationsApiDeleteTeam.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Team successfully deleted')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error deleting team')
    },
  })

  const { mutate: addTeam } = putApplicationsApiAddTeam.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Team successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding team')
    },
  })

  const invalidateQueries = () => {
    getApplicationsApiGetApplicationTeamsForUserOrganization.invalidateQueries({
      parameters: { path: { appId: applicationId || 0 } },
    })
    getApplicationsApiGetApplications.invalidateQueries({})
  }

  const submitHandler = useCallback(
    (data: ApplicationTeamForm) => {
      if (del) {
        const teamId = props.selectedTeam?.id

        deleteTeam({ query: { teamId } })
      } else if (edit && props.selectedTeam) {
        const updatedTeam: MessagesApplicationsApplicationTeam = {
          id: props.selectedTeam.id ?? 0,
          name: data.name,
          organisationId: props.selectedTeam.organisationId ?? 0,
          description: data.description || '',
          linkedProjectCount: props.selectedTeam.linkedProjectCount ?? 0,
          activeLinkedProjectCount: props.selectedTeam.activeLinkedProjectCount ?? 0,
          linkedUserCount: props.selectedTeam.linkedUserCount ?? 0,
          linkedRoleCount: props.selectedTeam.linkedRoleCount ?? 0,
          applicationId: applicationId || 0,
          typeName: props.selectedTeam.typeName || undefined,
        }

        updateTeam({
          body: updatedTeam,
        })
      } else {
        const newTeam: MessagesSubscriptionsNewOrganisationTeam = {
          id: 0,
          name: data.name,
          description: data.description || '',
          organisationId: data.organisationId,
          applicationId: applicationId || 0,
          typeName: undefined,
          linkedProjectCount: 0,
          activeLinkedProjectCount: 0,
          linkedUserCount: 0,
          linkedRoleCount: 0,
        }

        addTeam({
          body: newTeam,
        })
      }
    },
    [edit, props.selectedTeam, applicationId, addTeam, updateTeam, deleteTeam]
  )

  const loading = isApplicationsLoading || isOrganisationsLoading
  const error = isApplicationsError || isOrganisationsError

  if (loading) return <FormComponentLoading />
  if (error) return <ErrorComponent />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent
        {...props}
        submitHandler={submitHandler}
        applications={applications}
        organisations={organisations}
      />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} applications={applications} organisations={organisations} />
  )
}

const FormComponent = (
  props: ApplicationTeamFormProps & {
    submitHandler: (data: ApplicationTeamForm) => void
    applications?: MessagesApplicationsApplication[]
    organisations?: MessagesSubscriptionsOrganisation[]
  }
) => {
  const { applicationId } = useCurrentApplication()
  const selectedTeam: Partial<MessagesApplicationsApplicationTeam> = props.selectedTeam ?? {}
  const { name, description, organisationId } = selectedTeam
  const edit = !!props.selectedTeam
  const del = edit && props.delete

  const defaultValues = useMemo(() => {
    return {
      name: name ?? '',
      description: description ?? '',
      organisationId: organisationId ?? 0,
      applicationId: applicationId || 0,
    }
  }, [name, description, organisationId, applicationId])

  const form = useForm<ApplicationTeamForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Reset form when selectedTeam changes
  useEffect(() => {
    if (props.selectedTeam) {
      form.reset({
        name: props.selectedTeam.name ?? '',
        description: props.selectedTeam.description ?? '',
        organisationId: props.selectedTeam.organisationId ?? 0,
        applicationId: applicationId || 0,
      })
    }
  }, [props.selectedTeam, form, applicationId])

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                disabled={del}
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={del}
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={del}
                control={form.control}
                name="organisationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organisation</FormLabel>
                    {props.selectedTeam ? (
                      <FormControl>
                        <Input value={props.selectedTeam.organisation ?? 'Organisation'} readOnly disabled />
                      </FormControl>
                    ) : (
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        defaultValue={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an organisation" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {props.organisations?.map((org) => (
                            <SelectItem key={org.id.toString()} value={org.id.toString()}>
                              {org.name} ({org.longName})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                disabled={del}
                control={form.control}
                name="applicationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application</FormLabel>
                    {props.selectedTeam ? (
                      <FormControl>
                        <Input value={props.selectedTeam.application ?? 'Application'} readOnly disabled />
                      </FormControl>
                    ) : (
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        defaultValue={field.value?.toString()}
                        disabled
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an application" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {props.applications?.map((app) => (
                            <SelectItem key={app.applicationId.toString()} value={app.applicationId.toString()}>
                              {app.appName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">{del ? 'Delete Team' : edit ? 'Update Team' : 'Add Team'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
